import {createContext, useContext, useEffect, useState} from "react";
import * as yup from "yup";
import {useAppState} from "@/store";
import {
    PROPERTY_TYPES_WITH_FLOORS,
    PROPERTY_TYPES_WITH_OPT_SIZE,
    PROPERTY_TYPES_WITH_OPT_SQFT,
    PROPERTY_TYPES_WITH_SQFT,
    PROPERTY_TYPES_WITH_STORIES
} from "@/constants";
import {scrollToElement} from "@/helpers";

const ValidationContext = createContext({})

export function ValidationProvider({children}) {
    const [isValid, setIsValid] = useState(false)
    const [errors, setErrors] = useState([])
    const [showErrors, setShowErrors] = useState(false)

    const {state} = useAppState()

    const addressSchema = yup.object({
        "address": yup.string().trim()
            .required("Please select a valid address from the suggestions")
            .test('has-zip', "Selected address does not have a valid zip code", (val, ctx) => !!ctx.parent.zip)
            .test('has-street', "Selected address does not have a valid street", (val, ctx) => !!ctx.parent.street)
            .test('has-city', "Selected address does not have a valid city", (val, ctx) => !!ctx.parent.city)
            .test('has-state', "Selected address does not have a valid state", (val, ctx) => !!ctx.parent.state),
        "longitude": yup.string().optional(),
        "latitude": yup.string().optional(),
        "street": yup.string().trim().optional(),
        "city": yup.string().trim().optional(),
        "state": yup.string().trim().optional(),
        "zip": yup.string().trim().optional(),
        "propertyType": yup.string().trim().required("Property type is required"),
        "size": yup.string().trim().when('propertyType', {
            is: (val) => !PROPERTY_TYPES_WITH_OPT_SIZE.includes(val),
            then:(schema) => schema.required("Size is required")
        }),
        "floor": yup.mixed().when('propertyType', {
            is: (val) => PROPERTY_TYPES_WITH_FLOORS.includes(val),
            then: () =>
                yup
                    .number()
                    .transform((value, original) => (original === '' ? undefined : value))
                    .typeError('Must be a number')
                    .required('Floor number is required')
                    .integer('Must be a whole number')
                    .min(1, 'Must be 1–99')
                    .max(99, 'Must be 1–99'),
            otherwise: (schema) => schema.strip(),
        }),
        "sqft": yup.string().trim().when('propertyType', {
            is: (val) => !PROPERTY_TYPES_WITH_OPT_SQFT.includes(val) && PROPERTY_TYPES_WITH_SQFT.includes(val),
            then: (schema) => schema.required("Square feet is required"),
        }),
        "stories": yup.mixed().when('propertyType', {
            is: (val) => PROPERTY_TYPES_WITH_STORIES.includes(val),
            then: () =>
                yup
                    .number()
                    .transform((value, original) => (original === '' ? undefined : value))
                    .typeError('Must be a number')
                    .required('Number of stories is required')
                    .integer('Must be a whole number')
                    .min(1, 'Must be 1–9')
                    .max(9, 'Must be 1–9'),
            otherwise: (schema) => schema.strip(),
        }),
        "entranceDistance": yup.string().required("Parking distance is required"),

        "stairs": yup.boolean().when('floor', {
            is: (floor) => floor > 1,
            then: (schema) => {
                return schema.test('elevator-is-set', 'Please select either Elevator or Stairs', (val, ctx) => {
                    const {elevator} = ctx.parent
                    return val || elevator
                })
            }
        }),
        "elevator": yup.boolean().when('floor', {
            is: (floor) => floor > 1,
            then: (schema) => {
                return schema.test('stairs-is-set', 'Please select either Elevator or Stairs', (val, ctx) => {
                    const {stairs} = ctx.parent
                    return val || stairs
                })
            }
        }),
    })

    const phone_regex = /^\+1 \(\d{3}\) \d{3}-\d{4}$/

    const readiness_error = window.ein?.content?.step_4?.readiness?.error

    const schema = {
        1: yup.object({
            to: addressSchema,
            from: addressSchema,
            fullness: yup.object({
                how_packed: yup.string().required("Please select an option")
            })

        }),
        2: yup.object({
            special: yup.object({
                everything: yup.string().required("Please select an option"),
                packing: yup.string().required("Please select an option"),
                same_day: yup.string().required("Please select an option"),
                multiple_stops: yup.string().required("Please select an option"),
                move_story: yup.string().when('everything', {
                    is: (everything) => everything === 'no',
                    then:(schema) => schema.required("Please select an option"),
                }),
                move_story_amount: yup.string().when(['everything', 'move_story'], {
                    is: (everything, move_story) => everything === 'no' && move_story !== 'storage' && move_story !== 'other',
                    then:(schema) => schema.required("Please select an option"),
                }),
                move_story_storage_size: yup.string().when(['everything', 'move_story'], {
                    is: (everything, move_story) => everything === 'no' && move_story === 'storage',
                    then:(schema) => schema.required("Please select an option"),
                }),
                packing_rooms: yup.array().ensure().when('packing', {
                    is: (packing) => packing === 'yes',
                    then:(schema) => schema.min(1, "Please select an option"),
                }),
                num_stops: yup.string().when('multiple_stops', {
                    is: (multiple_stops) => multiple_stops === 'yes',
                    then:(schema) => schema.required("Please select an option"),
                })
            })
        }),
        3: yup.object({
            preferred_date: yup.object({
                date: yup.date().required(),
                formatted: yup.date().required(),
                raw: yup.date().required("Please choose your moving day"),
                flexibility: yup.string().required("Please select an option"),
            })
        }),
        4: yup.object({
            contact: yup.object({
                first_name: yup.string().trim().required("First name is required"),
                last_name: yup.string().trim().required("Last name is required"),
                email: yup.string().trim().email("Must be a valid email address").required("Email address is required"),
                phone: yup.string().trim().matches(phone_regex, 'Phone number is not valid').required("Phone number is required"),
                referred_by: yup.string().required("Please select a referral option"),
                accept_terms: yup.boolean().required("You must accept the terms and conditions").isTrue('You must accept the terms and conditions'),
                readiness: yup.string().required(readiness_error || "Please select an option")
            })
        }),
        6: yup.object({
            contact: yup.object({
                accept_estimate: yup.boolean().required("You must accept the estimate disclaimer").isTrue('You must accept the estimate disclaimer')
            })
        })
    }

    const validate = () => {

        if (!schema[state.currentStep]) {
            return
        }

        try {
            const validFields = schema[state.currentStep].validateSync(state, {stripUnknown: true, abortEarly: false})
            // console.log('valid', validFields)
            setIsValid(true)
            setErrors([])
        } catch (e) {
            const errorsMapped = e.inner.map(({message, path}) => {
                return {message, path}
            })
            // console.log('invalid', errorsMapped)
            setErrors(errorsMapped)
            setIsValid(false)
            return errorsMapped
        }
    }

    const preSubmitValidate = () => {

        setShowErrors(false)

        const errors = validate()

        if (typeof errors === "undefined") {
            return
        }

        setShowErrors(true)

        const form = document.querySelector('#ein-booking-form')
        const selector = errors.map(e => `[name="${e.path}"]`).join(',')
        const el = form.querySelector(selector)

        if (el) {
            scrollToElement(el, -250)
        }

        throw new Error(errors[0]?.message || 'Please ensure all required fields are filled out correctly.')

    }

    const isFieldValid = (path) => {
        return !errors.find(error => error.path === path)
    }

    const invalidMessage = (path) => {
        const error = errors.find(error => error.path === path)
        if (!error) {
            return false
        }
        return error.message
    }

    useEffect(() => {
        validate()
    }, [state]);

    return (
        <ValidationContext value={{isValid, isFieldValid, invalidMessage, validate, preSubmitValidate, showErrors}}>
            {children}
        </ValidationContext>
    )
}

export function useValidation() {
    return useContext(ValidationContext)
}
